/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { ANALYTICS, LEFT_NAV_KEYS, SKELETON_MENU_KEYS } from '../utils/enums';
import { analyticsSharedData } from './index';
import { getAppContentType } from './appTileAnalytics';
import { ANALYTICS_CONSTANTS } from '../utils/commonUtil';

class CatalogAnalytics {
    routingPath = '';

    sendAnalyticsBool = false;

    appsAnalyticsArray = [];

    sendAnalytics(
        appConfig, appsSkeletonModel, eventName, dataObject = {}, shouldSendLoadTime
    ) {
        let obj = {};
        const componentParams = {};
        obj.event = {};
        obj.ccd = {};
        obj.content = {};
        obj.ui = {};
        obj.env = {};
        obj.event.workflow = 'Apps';
        componentParams.sectionId = dataObject.appSection ? dataObject.appSection.id : null;
        componentParams.source = ANALYTICS.APPS;

        switch (eventName) {
        case ANALYTICS.EVENTS.RENDER_PAGE:
            obj.event.subcategory = dataObject.appSection && dataObject.appSection.id;
            obj.event.type = 'render';
            obj.event.subtype = 'page';
            obj.content.category = appsSkeletonModel.selectedSubSection;
            componentParams.shouldSendLoadTime = shouldSendLoadTime;
            break;
        case ANALYTICS.EVENTS.RENDER_CONNECTION_FAILED_PAGE:
            obj.event.subcategory = dataObject.appSection && dataObject.appSection.id;
            obj.event.type = 'render';
            obj.event.subtype = 'page';
            obj.event.value = dataObject.eventValue;
            break;
        case ANALYTICS.EVENTS.NO_CONNECTION_BTN_CLICK:
            obj.event.subcategory = dataObject.appSection && dataObject.appSection.id;
            obj.event.type = 'click';
            obj.event.subtype = dataObject.subType;
            obj.event.value = dataObject.eventValue;
            if (dataObject.app) {
                obj.content.id = dataObject.app.sapcode;
                obj.content.name = dataObject.app.appFamilyName;
                obj.content.version = dataObject.app.codexVersion;
            }
            break;
        case ANALYTICS.EVENTS.RENDER_APP:
            obj.event.type = 'render';
            obj.event.subtype = 'app';
            obj.event.subcategory = dataObject.appSection && dataObject.appSection.id;
            obj.ui.view_type = dataObject.viewType;
            obj.content.size = dataObject.contentSize;
            if (dataObject.app) {
                obj.content.category = dataObject.app.platform;
                obj.content.id = dataObject.app.sapcode;
                obj.content.name = dataObject.app.appFamilyName;
                obj.content.type = getAppContentType(dataObject);
                obj.content.version = dataObject.app.codexVersion;
                obj.content.extension = dataObject.contentExt;
                if (dataObject.app.isIncompatible === true) {
                    obj.event.value = 'Not Compatible';
                }
                obj.ccd = {
                    render_guid: `${String(dataObject.app.sapcode).toUpperCase()}_${dataObject.app.codexVersion}`,
                };
            }
            return obj;
        case ANALYTICS.EVENTS.APPS_ARRAY:
            if (dataObject.appsAnalyticsArray) {
                obj = dataObject.appsAnalyticsArray;
            } else if (analyticsSharedData.appsRenderAnalyticsArray.length > 0) {
                obj = analyticsSharedData.appsRenderAnalyticsArray;
            } else {
                return null;
            }
            break;
        case ANALYTICS.EVENTS.HEADER_BAR_BUTTON:
            obj.event.type = 'click';
            obj.event.subtype = dataObject.actionId;
            obj.event.subcategory = dataObject.appSection && dataObject.appSection.id;
            componentParams.actionId = dataObject.actionId;
            break;
        case ANALYTICS.EVENTS.HEADER_BAR_MENU_ITEM:
            obj.event.type = 'click';
            obj.event.subcategory = 'Updates:Operation Menu';
            if (dataObject && dataObject.actionId === SKELETON_MENU_KEYS.CFU_KEY) {
                obj.event.subtype = 'check-for-updates';
            } else if (dataObject && dataObject.actionId === SKELETON_MENU_KEYS.ENABLE_AU_KEY) {
                obj.event.subtype = 'auto-update';
            }
            obj.event.value = 'enable';
            break;
        case ANALYTICS.EVENTS.BANNER_EVENTS:
            obj.event.type = dataObject.type;
            obj.event.subtype = dataObject.subtype;
            obj.event.value = (dataObject.ingestDescription
                                || ((window.appContext && window.appContext.osLanguage === 'en') ? dataObject.CTAText : ANALYTICS_CONSTANTS.EN_STR_NOT_AVAILABLE));
            obj.event.subcategory = dataObject.sectionId;
            obj.content.category = dataObject.platform;
            obj.content.name = dataObject.name;
            obj.content.id = dataObject.ingestId;
            obj.ui.view_type = dataObject.viewType;
            obj.ui.filter = dataObject.uiFilter;
	        obj.ui.searchKeyword = dataObject.uiSearchKeyword;
            obj.event.guid = dataObject.guid;
            obj.content.action = dataObject.url;
            obj.content.version = dataObject.version;
            obj.event.context_guid = dataObject.context_guid;
            obj.event.context_sequence = dataObject.context_sequence;
            obj.ccd.render_guid = dataObject.renderGuid;
            obj.ccd.foreground_only = dataObject.foreground_only;
            obj.env.svc_name = dataObject.svc_name;
            componentParams.sectionId = dataObject.sectionId;
            if (dataObject.sectionId === LEFT_NAV_KEYS.ALL_APPS_KEY
                || dataObject.sectionId === LEFT_NAV_KEYS.UPDATES_KEY) {
                componentParams.surfaceType = ANALYTICS.SURFACE_TYPE.ALL_APPS_BANNER;
            } else {
                componentParams.surfaceType = ANALYTICS.SURFACE_TYPE.CATEGORY_BANNER;
            }
            break;
        default:
            return null;
        }
        const analyticObjectList = obj instanceof Array ? obj : [obj];
        appConfig.analyticsCallback({ componentParams, analyticObjectList });
        return null;
    }
}

export default new CatalogAnalytics();
